import React, { useState, useEffect } from 'react';
import { Megaphone, Send, Clock, Trash2, Globe, Users, Shield, AlertCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';

const AdminAnnouncements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newTarget, setNewTarget] = useState('All Users');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
      return;
    }

    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const data = await api.get('/announcements');
        setAnnouncements(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch announcements', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newTitle || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const data = await api.post('/announcements', {
        title: newTitle,
        target: newTarget,
        status: 'Live'
      });
      setAnnouncements(prev => Array.isArray(prev) ? [data, ...prev] : [data]);
      setNewTitle('');
    } catch (err) {
      console.error('Failed to post announcement', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      setAnnouncements(prev => Array.isArray(prev) ? prev.filter(a => a.id !== id) : []);
    } catch (err) {
      console.error('Failed to delete announcement', err);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Platform Announcements</h1>
        <p className="text-gray-500 font-medium mt-1 text-sm">Broadcast important updates and alerts to all or specific user groups.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm sticky top-24">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Megaphone size={18} className="text-brand-indigo" />
              New Broadcast
            </h2>
            <form onSubmit={handlePost} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-1.5">Announcement Title</label>
                <input 
                  type="text" 
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Scheduled System Upgrade"
                  className="w-full bg-gray-50 border-none rounded-2xl py-3.5 px-4 font-bold text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-1.5">Target Audience</label>
                <select 
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  className="w-full bg-gray-50 border-none rounded-2xl py-3.5 px-4 font-bold text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none appearance-none"
                >
                  <option>All Users</option>
                  <option>Speakers & Hosts</option>
                  <option>Community Admins</option>
                  <option>New Users</option>
                </select>
              </div>
              <button 
                type="submit"
                disabled={isSubmitting || !newTitle}
                className="w-full bg-brand-indigo text-white font-bold py-3.5 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                Broadcast Now
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">Recent Broadcasts</h2>
              <span className="text-xxs font-bold text-gray-400 uppercase tracking-widest">{Array.isArray(announcements) ? announcements.length : 0} total</span>
            </div>
            <div className="divide-y divide-gray-50">
              {loading ? (
                <div className="p-12 text-center text-gray-400">
                  <Loader2 size={24} className="animate-spin mx-auto mb-2" />
                  <p className="text-xs font-bold uppercase tracking-widest">Loading announcements...</p>
                </div>
              ) : (Array.isArray(announcements) && announcements.length > 0) ? (
                announcements.map((alert) => (
                  <div key={alert.id} className="p-5 hover:bg-gray-50/50 transition-all flex items-center justify-between group">
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm shrink-0",
                        alert.status === 'Live' ? "bg-green-50 text-green-600" : "bg-gray-50 text-gray-400"
                      )}>
                        <Megaphone size={18} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 leading-tight text-sm">{alert.title}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xxs font-bold text-brand-indigo uppercase tracking-widest">{alert.target}</span>
                          <span className="text-xxs font-bold text-gray-400">• {new Date(alert.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-xxs font-bold px-1.5 py-0.5 rounded-md uppercase tracking-widest",
                        alert.status === 'Live' ? "text-green-600 bg-green-50" : "text-gray-400 bg-gray-50"
                      )}>
                        {alert.status}
                      </span>
                      <button 
                        onClick={() => handleDelete(alert.id)}
                        className="p-2 text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-12 text-center text-gray-400">
                   <p className="text-sm font-medium">No announcements found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncements;
